import { onMount } from "solid-js"
import { useIsEngineModeGameMemo } from "../../../rx/memo/useIsEngineModeGameMemo"
import { useActionLogsSignal } from "../../../rx/signal/useActionLogsSignal"
import type { Nullable } from "../../../typescript"
import type { Engine } from "../../core/engine/Engine.type"
import getPlayerGraphNode from "../../core/engine/PlayerGraphNode/getPlayerGraphNode"
import type { PlayerGraphNode } from "../../core/engine/PlayerGraphNode/PlayerGraphNode.type"
import { EntityTypeId } from "../../core/entity/EntityTypeId.enum"
import isEntityHasMovesLeft from "../../core/entity/isEntityHasMovesLeft"
import type { PlayerEntity } from "../../core/player/PlayerEntity.type"
import findMostRecentActionLogPosition from "../../core/state/flux/ActionLog/findMostRecentActionLogPosition"
import { VERTICES_PER_TILE } from "../consts"
import { getEngineThreeControls } from "./getSetEngineThreeControls"
import { getEngineThreeMainCamera } from "./getSetEngineThreeMainCamera"
import setObjPosition from "./setObjPosition"

export default function onMountStartCameraNearLastAction(
  engine: Engine,
): void {
  const isEngineModeGame = useIsEngineModeGameMemo(engine)
  const [getActionLogs] = useActionLogsSignal(engine)
  onMount(() => {
    const camera = getEngineThreeMainCamera(engine)
    const mc = getEngineThreeControls(engine)
    const { state } = engine
    const actionLogs = getActionLogs()
    const centerTp = {
      x: (state.width) / 2,
      y: (state.height) / 2,
    }
    setObjPosition(camera, centerTp, VERTICES_PER_TILE * 24)
    mc.target.copy(camera.position)
    mc.target.y = 0

    // Default position if no action is found
    const lookAtTp = {
      x: 4,
      y: 6,
    }

    // move camera target to last action that has any form of {x, y}
    // excluding Lobby/Map actions if the game has started
    const mostRecentActionLogPosition = findMostRecentActionLogPosition(actionLogs, isEngineModeGame())

    if (mostRecentActionLogPosition) {
      lookAtTp.x = mostRecentActionLogPosition.x
      lookAtTp.y = mostRecentActionLogPosition.y
    } else if (isEngineModeGame()) {
      const playerId = state.turnPlayerId;
      const playerGraphNode = getPlayerGraphNode(engine, playerId);
      const entityToFocus = findBestEntityForCamera(playerGraphNode)
      
      if (entityToFocus) {
        lookAtTp.x = entityToFocus.x;
        lookAtTp.y = entityToFocus.y;
      }
    }

    const cameraAtTp = {
      x: lookAtTp.x,
      y: lookAtTp.y - 2,
    }

    setObjPosition(camera, lookAtTp, VERTICES_PER_TILE * 0)
    mc.target.copy(camera.position)
    setObjPosition(camera, cameraAtTp, VERTICES_PER_TILE * 4)
    mc.target.y = 0
  })
}

function findBestEntityForCamera(playerGraphNode: PlayerGraphNode): Nullable<PlayerEntity> {
  const ents: PlayerEntity[] = playerGraphNode.ents()
  
  // 1. Find next available unit with moves left
  const availableUnit = ents.find(isEntityHasMovesLeft)
  if (availableUnit) {
    return availableUnit
  }
  
  // 2. Find factory that hasn't built this turn
  const availableFactory = ents.find((entity: PlayerEntity) => 
    entity.etype_id === EntityTypeId.Factory && 
    !(entity as { builtThisTurn?: boolean }).builtThisTurn
  )
  if (availableFactory) {
    return availableFactory
  }
  
  // 3. Find any HQ
  const hq = ents.find(
    (entity: PlayerEntity) => entity.etype_id === EntityTypeId.HQ
  )
  if (hq) {
    return hq
  }
  
  // 4. Fallback to any entity
  return ents[0]
}